
/**
 * Extensions Page - Data Table Implementation
 * Refactored for high data density and better information hierarchy.
 */

import { useState, useMemo } from 'react'
import {
  Plus,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useExtensions,
  useCreateExtension,
  useUpdateExtension,
  useDeleteExtension,
} from '@/hooks'
import { useUIStore } from '@/stores/ui-store'
import type { ExtensionStatus } from '@/lib/types'
import { RegisterExtensionModal } from './RegisterExtensionModal'
import { GenerateLicenseModal } from './GenerateLicenseModal'
import { ExtensionsStats } from './ExtensionsStats'
import { ExtensionsFilters } from './ExtensionsFilters'
import { ExtensionsList } from './ExtensionsList'
import type { FilterStatus } from '../types'

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export function ExtensionsPage() {
  const { data: extensions = [], isLoading, refetch } = useExtensions()
  const createMutation = useCreateExtension()
  const updateMutation = useUpdateExtension()
  const deleteMutation = useDeleteExtension()

  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const { modalOpen, openModal, closeModal, selectedExtensionId, setSelectedExtensionId } = useUIStore()

  // Stats
  const stats = useMemo(() => ({
    total: extensions.length,
    approved: extensions.filter((e: any) => e.status === 'approved').length,
    pending: extensions.filter((e: any) => e.status === 'pending').length,
  }), [extensions])

  // Categories
  const categories = useMemo(() =>
    Array.from(new Set(extensions.map((e: any) => e.category).filter(Boolean))),
    [extensions]
  )

  // Filtering
  const filteredExtensions = useMemo(() => {
    return extensions.filter((ext: any) => {
      const matchesSearch = searchQuery === '' ||
        ext.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ext.description || '').toLowerCase().includes(searchQuery.toLowerCase())

      const matchesStatus = filterStatus === 'all' || ext.status === filterStatus
      const matchesCategory = filterCategory === 'all' || ext.category === filterCategory

      return matchesSearch && matchesStatus && matchesCategory
    })
  }, [extensions, searchQuery, filterStatus, filterCategory])

  // Pagination
  const totalPages = Math.ceil(filteredExtensions.length / itemsPerPage)
  const paginatedExtensions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredExtensions.slice(startIndex, endIndex)
  }, [filteredExtensions, currentPage, itemsPerPage])

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1)
  }, [searchQuery, filterStatus, filterCategory])

  const handleStatusChange = (id: string, status: ExtensionStatus) => {
    if (confirm(`Change status to ${status}?`)) {
      updateMutation.mutate({ id, data: { status } } as any)
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('Delete this extension?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleGenerateLicense = (id: string) => {
    setSelectedExtensionId(id)
    openModal('generate-license')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Extensions</h2>
          <p className="text-sm text-muted-foreground">Manage marketplace items and review submissions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} className="h-9">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => openModal('create-extension')} className="h-9">
            <Plus className="h-4 w-4 mr-2" />
            Register Extension
          </Button>
        </div>
      </div>

      <ExtensionsStats stats={stats} />

      <ExtensionsFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        categories={categories as string[]}
      />

      <ExtensionsList
        extensions={paginatedExtensions}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalCount={filteredExtensions.length}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
        onGenerateLicense={handleGenerateLicense}
      />

      <RegisterExtensionModal
        isOpen={modalOpen.type === 'create-extension'}
        onClose={closeModal}
        createExtension={createMutation.mutate}
        isPending={createMutation.isPending}
      />

      <GenerateLicenseModal
        isOpen={modalOpen.type === 'generate-license'}
        onClose={closeModal}
        extensionId={selectedExtensionId}
      />
    </div>
  )
}
